import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/lib/api';

interface AdminUser {
  id: string;
  email: string;
  display_name: string | null;
  plan_id: string;
  role: string;
  status: string;
  created_at: number;
  expiry_at: number | null;
  login_count: number;
  last_login_at: number | null;
}

interface AdminUsersResponse {
  users: AdminUser[];
  total: number;
  page: number;
  page_size: number;
}

const PLAN_OPTIONS = [
  { id: 'free', name: 'Free' },
  { id: 'plus', name: 'Plus' },
  { id: 'pro', name: 'Pro' },
];

const STATUS_OPTIONS = [
  { id: 'active', name: 'Hoạt động' },
  { id: 'suspended', name: 'Tạm khóa' },
];

const ROLE_OPTIONS = [
  { id: 'user', name: 'User' },
  { id: 'admin', name: 'Admin' },
  { id: 'superadmin', name: 'Super Admin' },
];

export function Admin() {
  const { isAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState<'users' | 'plans' | 'sepay' | 'audit' | 'vouchers'>('users');

  // Users state
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [usersLoading, setUsersLoading] = useState(true);
  const [usersError, setUsersError] = useState('');
  const [usersTotal, setUsersTotal] = useState(0);
  const [usersPage, setUsersPage] = useState(1);
  const usersPageSize = 20;
  const [search, setSearch] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [savingUser, setSavingUser] = useState<string | null>(null);
  const [saveUserErrors, setSaveUserErrors] = useState<Record<string, string>>({});
  const [selectedUserDetail, setSelectedUserDetail] = useState<any | null>(null);
  const [userDetailLoading, setUserDetailLoading] = useState(false);

  // Plans state
  const [plans, setPlans] = useState<any[]>([]);
  const [plansLoading, setPlansLoading] = useState(false);
  const [plansError, setPlansError] = useState('');
  const [savingPlan, setSavingPlan] = useState<string | null>(null);

  // SePay state
  const [sepayLoading, setSepayLoading] = useState(false);
  const [sepayError, setSepayError] = useState('');
  const [sepaySaving, setSepaySaving] = useState(false);
  const [sepayForm, setSepayForm] = useState({
    acc: '', bank: '', template: '', holder: '', store: '', webhook_secret: '', webhook_url: ''
  });

  // Audit logs state
  const [auditLogs, setAuditLogs] = useState<any[]>([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditError, setAuditError] = useState('');
  const [auditTotal, setAuditTotal] = useState(0);
  const [auditPage, setAuditPage] = useState(1);
  const auditPageSize = 50;
  const [auditUserFilter, setAuditUserFilter] = useState('');
  const [auditActionFilter, setAuditActionFilter] = useState('');

  // Vouchers state
  interface VoucherFormData {
    code: string;
    type: 'percent' | 'days' | 'plan_upgrade';
    value: number;
    target_plan: 'plus' | 'pro' | null;
    max_uses: number;
    per_user_limit: number;
    valid_from: number;
    valid_until: number;
    is_active: boolean;
    metadata: { applicable_plans: ('plus' | 'pro')[]; min_amount: number };
  }

  const [vouchers, setVouchers] = useState<any[]>([]);
  const [vouchersLoading, setVouchersLoading] = useState(false);
  const [vouchersError, setVouchersError] = useState('');
  const [vouchersTotal, setVouchersTotal] = useState(0);
  const [vouchersPage, setVouchersPage] = useState(1);
  const vouchersPageSize = 20;
  const [voucherModalOpen, setVoucherModalOpen] = useState(false);
  const [voucherEditing, setVoucherEditing] = useState<any | null>(null);
  const [voucherForm, setVoucherForm] = useState<VoucherFormData>({
    code: '', type: 'percent', value: 10, target_plan: null, max_uses: 100, per_user_limit: 1,
    valid_from: Date.now(), valid_until: Date.now() + 30*24*60*60*1000, is_active: true,
    metadata: { applicable_plans: [], min_amount: 0 }
  });
  const [voucherSaving, setVoucherSaving] = useState(false);
  const [voucherError, setVoucherError] = useState('');

  // ===== Users =====
  const loadUsers = useCallback(async () => {
    setUsersLoading(true);
    setUsersError('');
    try {
      const data: AdminUsersResponse = await api.adminGetUsers(usersPage, usersPageSize, search, planFilter, statusFilter);
      setUsers(data.users);
      setUsersTotal(data.total);
    } catch (err) {
      setUsersError(err instanceof Error ? err.message : 'Không thể tải danh sách user');
    } finally {
      setUsersLoading(false);
    }
  }, [usersPage, search, planFilter, statusFilter]);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const handlePlanChange = async (userId: string, planId: string) => {
    setSavingUser(userId);
    setSaveUserErrors(prev => ({ ...prev, [userId]: '' }));
    try {
      await api.adminUpdateUserPlan(userId, planId);
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, plan_id: planId } : u));
    } catch (err) {
      setSaveUserErrors(prev => ({ ...prev, [userId]: err instanceof Error ? err.message : 'Cập nhật thất bại' }));
    } finally { setSavingUser(null); }
  };

  const handleStatusChange = async (userId: string, status: string) => {
    setSavingUser(userId);
    setSaveUserErrors(prev => ({ ...prev, [userId]: '' }));
    try {
      await api.adminUpdateUserStatus(userId, status);
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, status } : u));
    } catch (err) {
      setSaveUserErrors(prev => ({ ...prev, [userId]: err instanceof Error ? err.message : 'Cập nhật thất bại' }));
    } finally { setSavingUser(null); }
  };

  const handleExpiryChange = async (userId: string, expiryAt: number | null) => {
    setSavingUser(userId);
    setSaveUserErrors(prev => ({ ...prev, [userId]: '' }));
    try {
      await api.adminUpdateUserExpiry(userId, expiryAt);
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, expiry_at: expiryAt } : u));
    } catch (err) {
      setSaveUserErrors(prev => ({ ...prev, [userId]: err instanceof Error ? err.message : 'Cập nhật thất bại' }));
    } finally { setSavingUser(null); }
  };

  const handleRoleChange = async (userId: string, role: string) => {
    setSavingUser(userId);
    setSaveUserErrors(prev => ({ ...prev, [userId]: '' }));
    try {
      await api.adminUpdateUserRole(userId, role);
      setUsers(prev => prev.map(u => u.id === userId ? { ...u, role } : u));
    } catch (err) {
      setSaveUserErrors(prev => ({ ...prev, [userId]: err instanceof Error ? err.message : 'Cập nhật thất bại' }));
    } finally { setSavingUser(null); }
  };

  const handleClearDevice = async (userId: string) => {
    if (!confirm('Xóa device binding? User sẽ có thể đăng nhập thiết bị mới.')) return;
    setSavingUser(userId);
    try {
      await api.adminClearUserDevice(userId);
      alert('Đã xóa device binding');
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Xóa thất bại');
    } finally { setSavingUser(null); }
  };

  const loadUserDetail = async (userId: string) => {
    setUserDetailLoading(true);
    try {
      const detail = await api.adminGetUserDetail(userId);
      setSelectedUserDetail(detail);
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Không thể tải chi tiết');
    } finally { setUserDetailLoading(false); }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setUsersPage(1);
    loadUsers();
  };

  // ===== Plans =====
  const loadPlans = useCallback(async () => {
    setPlansLoading(true);
    try {
      const data = await api.adminGetPlans();
      setPlans(data.plans);
    } catch (err) {
      setPlansError(err instanceof Error ? err.message : 'Không thể tải gói');
    } finally { setPlansLoading(false); }
  }, []);

  useEffect(() => { if (activeTab === 'plans') loadPlans(); }, [activeTab, loadPlans]);

  const handlePlanUpdate = async (planId: string, updates: any) => {
    setSavingPlan(planId);
    try {
      const res = await api.adminUpdatePlan(planId, updates);
      setPlans(prev => prev.map(p => p.id === planId ? res.plan : p));
      alert('Cập nhật thành công');
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Cập nhật thất bại');
    } finally { setSavingPlan(null); }
  };

  // ===== SePay =====
  const loadSepayConfig = useCallback(async () => {
    setSepayLoading(true);
    try {
      const data = await api.adminGetSepayConfig();
      if (data.config) {
        setSepayForm({
          acc: data.config.account_number || '',
          bank: data.config.bank_code || '',
          template: data.config.template || '',
          holder: data.config.account_name || '',
          store: data.config.store_name || '',
          webhook_secret: '',
          webhook_url: data.config.webhook_url || ''
        });
      }
    } catch (err) {
      setSepayError(err instanceof Error ? err.message : 'Không thể tải config');
    } finally { setSepayLoading(false); }
  }, []);

  useEffect(() => { if (activeTab === 'sepay') loadSepayConfig(); }, [activeTab, loadSepayConfig]);

  const handleSepaySave = async () => {
    setSepaySaving(true);
    try {
      await api.adminUpdateSepayConfig(sepayForm);
      alert('Lưu SePay config thành công');
      loadSepayConfig();
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Lưu thất bại');
    } finally { setSepaySaving(false); }
  };

  // ===== Audit Logs =====
  const loadAuditLogs = useCallback(async () => {
    setAuditLoading(true);
    try {
      const data = await api.adminGetAuditLogs(auditUserFilter || undefined, auditActionFilter || undefined, auditPage, auditPageSize);
      setAuditLogs(data.logs);
      setAuditTotal(data.total);
    } catch (err) {
      setAuditError(err instanceof Error ? err.message : 'Không thể tải audit logs');
    } finally { setAuditLoading(false); }
  }, [auditPage, auditUserFilter, auditActionFilter]);

  useEffect(() => { if (activeTab === 'audit') loadAuditLogs(); }, [activeTab, loadAuditLogs]);

  // ===== Vouchers =====
  const loadVouchers = useCallback(async () => {
    setVouchersLoading(true);
    try {
      const data = await api.adminGetVouchers(vouchersPage, vouchersPageSize);
      setVouchers(data.vouchers);
      setVouchersTotal(data.total);
    } catch (err) {
      setVouchersError(err instanceof Error ? err.message : 'Không thể tải voucher');
    } finally { setVouchersLoading(false); }
  }, [vouchersPage]);

  useEffect(() => { if (activeTab === 'vouchers') loadVouchers(); }, [activeTab, loadVouchers]);

  const openVoucherModal = (voucher?: any) => {
    if (voucher) {
      setVoucherEditing(voucher);
      setVoucherForm({
        code: voucher.code,
        type: voucher.type || voucher.voucher_type,
        value: voucher.value,
        target_plan: voucher.target_plan || null,
        max_uses: voucher.max_uses,
        per_user_limit: voucher.per_user_limit,
        valid_from: voucher.valid_from,
        valid_until: voucher.valid_until,
        is_active: voucher.is_active,
        metadata: voucher.metadata || { applicable_plans: [], min_amount: 0 }
      });
    } else {
      setVoucherEditing(null);
      setVoucherForm({
        code: '', type: 'percent', value: 10, target_plan: null, max_uses: 100, per_user_limit: 1,
        valid_from: Date.now(), valid_until: Date.now() + 30*24*60*60*1000, is_active: true,
        metadata: { applicable_plans: [], min_amount: 0 }
      });
    }
    setVoucherError('');
    setVoucherModalOpen(true);
  };

  const handleVoucherSave = async () => {
    setVoucherSaving(true);
    try {
      if (voucherEditing) {
        // For updates, exclude code from the updates
        const { code, ...updates } = voucherForm;
        await api.adminUpdateVoucher(code, updates);
      } else {
        await api.adminCreateVoucher(voucherForm);
      }
      alert(voucherEditing ? 'Cập nhật thành công' : 'Tạo voucher thành công');
      setVoucherModalOpen(false);
      loadVouchers();
    } catch (err) {
      setVoucherError(err instanceof Error ? err.message : 'Lưu thất bại');
    } finally { setVoucherSaving(false); }
  };

  const handleVoucherToggle = async (code: string, currentStatus: boolean) => {
    if (!confirm(`${currentStatus ? 'Vô hiệu hóa' : 'Kích hoạt'} voucher này?`)) return;
    try {
      await api.adminToggleVoucher(code);
      setVouchers(prev => prev.map(v => v.code === code ? { ...v, is_active: !currentStatus } : v));
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Thao tác thất bại');
    }
  };

  const handleVoucherDelete = async (code: string) => {
    if (!confirm('Xóa voucher này? Không thể hoàn tác.')) return;
    try {
      await api.adminDeleteVoucher(code);
      setVouchers(prev => prev.filter(v => v.code !== code));
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Xóa thất bại');
    }
  };

  const formatDate = (timestamp: number | null) => {
    if (!timestamp) return 'N/A';
    return new Date(timestamp).toLocaleString('vi-VN');
  };

  if (!isAdmin) return null;

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container max-w-7xl">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-text mb-2">Quản trị viên</h1>
          <p className="text-text-muted">Quản lý người dùng, gói, thanh toán, voucher & logs</p>
        </div>

        {/* Tabs */}
        <div className="mb-6 border-b border-border">
          <nav className="flex gap-1" role="tablist">
            {[
              { id: 'users', label: 'Người dùng', icon: '👥' },
              { id: 'plans', label: 'Gói cước', icon: '📦' },
              { id: 'sepay', label: 'SePay QR', icon: '💳' },
              { id: 'vouchers', label: 'Voucher', icon: '🎫' },
              { id: 'audit', label: 'Audit Logs', icon: '📋' },
            ].map(tab => (
              <button
                key={tab.id}
                role="tab"
                aria-selected={activeTab === tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`px-4 py-2 rounded-t-lg text-sm font-medium transition-colors ${
                  activeTab === tab.id
                    ? 'bg-white text-primary border-b-2 border-primary -mb-px'
                    : 'text-text-muted hover:text-text hover:bg-gray-100'
                }`}
              >
                {tab.icon} {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === 'users' && (
          <div className="card">
            {usersError && <div className="m-4 p-4 text-red-600 bg-red-50 rounded-lg">{usersError}</div>}

            {/* Filters */}
            <div className="p-6 border-b border-border">
              <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-4">
                <div className="flex-1 min-w-[200px]">
                  <label className="label">Tìm kiếm</label>
                  <input type="text" value={search} onChange={e => setSearch(e.target.value)} className="input w-full mt-1" placeholder="Email, tên, ID..." />
                </div>
                <div className="min-w-[150px]">
                  <label className="label">Gói</label>
                  <select value={planFilter} onChange={e => { setPlanFilter(e.target.value); setUsersPage(1); loadUsers(); }} className="input w-full mt-1">
                    <option value="">Tất cả</option>
                    {PLAN_OPTIONS.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
                <div className="min-w-[150px]">
                  <label className="label">Trạng thái</label>
                  <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setUsersPage(1); loadUsers(); }} className="input w-full mt-1">
                    <option value="">Tất cả</option>
                    {STATUS_OPTIONS.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
              </form>
            </div>

            {/* Users Table */}
            <div className="overflow-x-auto">
              {usersLoading ? (
                <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-4 border-primary border-t-transparent" /></div>
              ) : users.length === 0 ? (
                <div className="text-center py-12 text-text-muted">Không tìm thấy người dùng</div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-text-muted border-b border-border bg-gray-50">
                      <th className="p-4 font-medium">ID</th>
                      <th className="p-4 font-medium">Email / Tên</th>
                      <th className="p-4 font-medium">Gói</th>
                      <th className="p-4 font-medium">Vai trò</th>
                      <th className="p-4 font-medium">Trạng thái</th>
                      <th className="p-4 font-medium">Hết hạn</th>
                      <th className="p-4 font-medium">Đăng nhập cuối</th>
                      <th className="p-4 font-medium">Tạo lúc</th>
                      <th className="p-4 font-medium">Thao tác</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map(user => (
                      <tr key={user.id} className="border-b border-border/50 hover:bg-gray-50">
                        <td className="p-4 font-mono text-text-muted">{user.id.slice(0, 8)}...</td>
                        <td className="p-4">
                          <div className="font-medium text-text">{user.email}</div>
                          {user.display_name && <div className="text-text-muted text-xs">{user.display_name}</div>}
                        </td>
                        <td className="p-4">
                          <select value={user.plan_id} onChange={e => handlePlanChange(user.id, e.target.value)} disabled={savingUser === user.id} className="input text-sm py-1">
                            {PLAN_OPTIONS.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                          </select>
                        </td>
                        <td className="p-4">
                          <select value={user.role} onChange={e => handleRoleChange(user.id, e.target.value)} disabled={savingUser === user.id} className="input text-sm py-1">
                            {ROLE_OPTIONS.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                          </select>
                        </td>
                        <td className="p-4">
                          <select value={user.status} onChange={e => handleStatusChange(user.id, e.target.value)} disabled={savingUser === user.id} className="input text-sm py-1">
                            {STATUS_OPTIONS.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                          </select>
                        </td>
                        <td className="p-4">
                          <input type="datetime-local" value={user.expiry_at ? new Date(user.expiry_at).toISOString().slice(0, 16) : ''} onChange={e => handleExpiryChange(user.id, e.target.value ? new Date(e.target.value).getTime() : null)} disabled={savingUser === user.id} className="input text-sm py-1 max-w-xs" />
                        </td>
                        <td className="p-4 text-text-muted">{formatDate(user.last_login_at)}</td>
                        <td className="p-4 text-text-muted">{formatDate(user.created_at)}</td>
                        <td className="p-4">
                          <div className="flex items-center gap-2">
                            {saveUserErrors[user.id] && <span className="text-red-600 text-xs">{saveUserErrors[user.id]}</span>}
                            {savingUser === user.id && <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary border-t-transparent" />}
                            <button onClick={() => loadUserDetail(user.id)} className="btn-outline text-sm py-1" disabled={userDetailLoading}>Chi tiết</button>
                            <button onClick={() => handleClearDevice(user.id)} className="btn-outline text-sm py-1 text-red-600 hover:bg-red-50" disabled={savingUser === user.id}>Xóa Device</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* User Detail Modal */}
            {selectedUserDetail && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedUserDetail(null)}>
                <div className="bg-white rounded-xl max-w-4xl w-full max-h-[80vh] overflow-auto" onClick={e => e.stopPropagation()}>
                  <div className="p-6 border-b border-border flex justify-between items-center">
                    <h2 className="text-xl font-bold">Chi tiết User: {selectedUserDetail.user.email}</h2>
                    <button onClick={() => setSelectedUserDetail(null)} className="text-text-muted hover:text-text">✕</button>
                  </div>
                  <div className="p-6 space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div><label className="label">Sessions</label><pre className="text-xs bg-gray-100 p-3 rounded max-h-40 overflow-auto">{JSON.stringify(selectedUserDetail.sessions, null, 2)}</pre></div>
                      <div><label className="label">Usage Logs</label><pre className="text-xs bg-gray-100 p-3 rounded max-h-40 overflow-auto">{JSON.stringify(selectedUserDetail.usage_logs, null, 2)}</pre></div>
                      <div><label className="label">Voucher Usage</label><pre className="text-xs bg-gray-100 p-3 rounded max-h-40 overflow-auto">{JSON.stringify(selectedUserDetail.voucher_usage, null, 2)}</pre></div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Pagination */}
            {!usersLoading && usersTotal > 0 && Math.ceil(usersTotal / usersPageSize) > 1 && (
              <div className="p-6 border-t border-border flex items-center justify-between">
                <div className="text-text-muted text-sm">Hiển thị {(usersPage - 1) * usersPageSize + 1} - {Math.min(usersPage * usersPageSize, usersTotal)} của {usersTotal}</div>
                <div className="flex gap-2">
                  <button onClick={() => setUsersPage(p => Math.max(1, p - 1))} disabled={usersPage === 1} className="btn-outline text-sm">Trước</button>
                  <span className="flex items-center px-3 text-sm text-text">Trang {usersPage} / {Math.ceil(usersTotal / usersPageSize)}</span>
                  <button onClick={() => setUsersPage(p => Math.min(Math.ceil(usersTotal / usersPageSize), p + 1))} disabled={usersPage === Math.ceil(usersTotal / usersPageSize)} className="btn-outline text-sm">Sau</button>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'plans' && (
          <div className="card">
            {plansError && <div className="m-4 p-4 text-red-600 bg-red-50 rounded-lg">{plansError}</div>}
            <div className="p-6">
              <h2 className="text-xl font-bold mb-4">Quản lý gói cước</h2>
              {plansLoading ? (
                <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-4 border-primary border-t-transparent" /></div>
              ) : (
                <div className="space-y-4">
                  {plans.map(plan => (
                    <div key={plan.id} className="border border-border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="font-bold text-lg">{plan.display_name} ({plan.id})</h3>
                        <span className="text-sm text-text-muted">{plan.price_monthly > 0 ? plan.price_monthly.toLocaleString() + 'đ/tháng' : 'Miễn phí'}</span>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                        <div>
                          <label className="label">Tên hiển thị</label>
                          <input type="text" defaultValue={plan.display_name} onChange={e => handlePlanUpdate(plan.id, { display_name: e.target.value })} className="input w-full mt-1" />
                        </div>
                        <div>
                          <label className="label">Giá tháng (VND)</label>
                          <input type="number" defaultValue={plan.price_monthly} onChange={e => handlePlanUpdate(plan.id, { price_monthly: Number(e.target.value) })} className="input w-full mt-1" disabled={savingPlan === plan.id} />
                        </div>
                      </div>
                      <div className="mb-4">
                        <label className="label">Features (mỗi dòng 1 feature)</label>
                        <textarea defaultValue={plan.features?.join('\n') || ''} onChange={e => handlePlanUpdate(plan.id, { features: e.target.value.split('\n').filter(f => f.trim()) })} className="input w-full mt-1 min-h-[100px]" />
                      </div>
                      <div>
                        <label className="label">Limits (JSON)</label>
                        <textarea defaultValue={JSON.stringify(plan.limits || {}, null, 2)} onChange={e => { try { handlePlanUpdate(plan.id, { limits: JSON.parse(e.target.value) }); } catch {} }} className="input w-full mt-1 min-h-[80px] font-mono text-sm" />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'sepay' && (
          <div className="card">
            {sepayError && <div className="m-4 p-4 text-red-600 bg-red-50 rounded-lg">{sepayError}</div>}
            <div className="p-6">
              <h2 className="text-xl font-bold mb-4">Cấu hình SePay QR</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="label">Số tài khoản</label>
                  <input type="text" value={sepayForm.acc} onChange={e => setSepayForm(prev => ({ ...prev, acc: e.target.value }))} className="input w-full mt-1" />
                </div>
                <div>
                  <label className="label">Tên chủ tài khoản</label>
                  <input type="text" value={sepayForm.holder} onChange={e => setSepayForm(prev => ({ ...prev, holder: e.target.value }))} className="input w-full mt-1" />
                </div>
                <div>
                  <label className="label">Mã ngân hàng</label>
                  <input type="text" value={sepayForm.bank} onChange={e => setSepayForm(prev => ({ ...prev, bank: e.target.value }))} className="input w-full mt-1" />
                </div>
                <div>
                  <label className="label">Tên cửa hàng</label>
                  <input type="text" value={sepayForm.store} onChange={e => setSepayForm(prev => ({ ...prev, store: e.target.value }))} className="input w-full mt-1" />
                </div>
                <div>
                  <label className="label">Template QR</label>
                  <input type="text" value={sepayForm.template} onChange={e => setSepayForm(prev => ({ ...prev, template: e.target.value }))} className="input w-full mt-1" />
                </div>
                <div>
                  <label className="label">Webhook URL</label>
                  <input type="text" value={sepayForm.webhook_url} onChange={e => setSepayForm(prev => ({ ...prev, webhook_url: e.target.value }))} className="input w-full mt-1" />
                </div>
                <div>
                  <label className="label">Webhook Secret (để trống để giữ nguyên)</label>
                  <input type="password" value={sepayForm.webhook_secret} onChange={e => setSepayForm(prev => ({ ...prev, webhook_secret: e.target.value }))} className="input w-full mt-1" />
                </div>
              </div>
              <div className="mt-6 flex gap-4">
                <button onClick={handleSepaySave} disabled={sepaySaving} className="btn-primary">{sepaySaving ? 'Đang lưu...' : 'Lưu cấu hình'}</button>
                <button onClick={loadSepayConfig} disabled={sepayLoading} className="btn-outline">{sepayLoading ? 'Đang tải...' : 'Tải lại'}</button>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'vouchers' && (
          <div className="card">
            {vouchersError && <div className="m-4 p-4 text-red-600 bg-red-50 rounded-lg">{vouchersError}</div>}
            <div className="p-6 border-b border-border flex justify-between items-center">
              <h2 className="text-xl font-bold">Quản lý Voucher</h2>
              <button onClick={() => openVoucherModal()} className="btn-primary">+ Tạo voucher mới</button>
            </div>
            <div className="overflow-x-auto">
              {vouchersLoading ? (
                <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-4 border-primary border-t-transparent" /></div>
              ) : vouchers.length === 0 ? (
                <div className="text-center py-12 text-text-muted">Chưa có voucher nào</div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-text-muted border-b border-border bg-gray-50">
                      <th className="p-4 font-medium">Mã</th>
                      <th className="p-4 font-medium">Loại</th>
                      <th className="p-4 font-medium">Giá trị</th>
                      <th className="p-4 font-medium">Gói đích</th>
                      <th className="p-4 font-medium">Max uses</th>
                      <th className="p-4 font-medium">Đã dùng</th>
                      <th className="p-4 font-medium">Hiệu lực</th>
                      <th className="p-4 font-medium">Trạng thái</th>
                      <th className="p-4 font-medium">Thao tác</th>
                    </tr>
                  </thead>
                  <tbody>
                    {vouchers.map(v => (
                      <tr key={v.code} className="border-b border-border/50 hover:bg-gray-50">
                        <td className="p-4 font-mono">{v.code}</td>
                        <td className="p-4">{v.type || v.voucher_type}</td>
                        <td className="p-4">{v.type === 'percent' ? v.value + '%' : v.type === 'days' ? v.value + ' ngày' : 'Nâng cấp'}</td>
                        <td className="p-4">{v.target_plan || 'Tất cả'}</td>
                        <td className="p-4">{v.max_uses}</td>
                        <td className="p-4">{v.used_count}</td>
                        <td className="p-4 text-text-muted">{formatDate(v.valid_from)} → {formatDate(v.valid_until)}</td>
                        <td className="p-4">
                          <span className={`px-2 py-1 rounded text-xs ${v.is_active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}>
                            {v.is_active ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td className="p-4">
                          <div className="flex gap-2">
                            <button onClick={() => openVoucherModal(v)} className="btn-outline text-sm py-1">Sửa</button>
                            <button onClick={() => handleVoucherToggle(v.code, v.is_active)} className="btn-outline text-sm py-1">{v.is_active ? 'Vô hiệu' : 'Kích hoạt'}</button>
                            <button onClick={() => handleVoucherDelete(v.code)} className="btn-outline text-sm py-1 text-red-600 hover:bg-red-50">Xóa</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            {/* Pagination */}
            {!vouchersLoading && vouchersTotal > 0 && Math.ceil(vouchersTotal / vouchersPageSize) > 1 && (
              <div className="p-6 border-t border-border flex items-center justify-between">
                <div className="text-text-muted text-sm">Hiển thị {(vouchersPage - 1) * vouchersPageSize + 1} - {Math.min(vouchersPage * vouchersPageSize, vouchersTotal)} của {vouchersTotal}</div>
                <div className="flex gap-2">
                  <button onClick={() => setVouchersPage(p => Math.max(1, p - 1))} disabled={vouchersPage === 1} className="btn-outline text-sm">Trước</button>
                  <span className="flex items-center px-3 text-sm text-text">Trang {vouchersPage} / {Math.ceil(vouchersTotal / vouchersPageSize)}</span>
                  <button onClick={() => setVouchersPage(p => Math.min(Math.ceil(vouchersTotal / vouchersPageSize), p + 1))} disabled={vouchersPage === Math.ceil(vouchersTotal / vouchersPageSize)} className="btn-outline text-sm">Sau</button>
                </div>
              </div>
            )}

            {/* Voucher Modal */}
            {voucherModalOpen && (
              <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setVoucherModalOpen(false)}>
                <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-auto" onClick={e => e.stopPropagation()}>
                  <div className="p-6 border-b border-border flex justify-between items-center">
                    <h2 className="text-xl font-bold">{voucherEditing ? 'Chỉnh sửa' : 'Tạo'} Voucher</h2>
                    <button onClick={() => setVoucherModalOpen(false)} className="text-text-muted hover:text-text">✕</button>
                  </div>
                  <div className="p-6 space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="label">Mã voucher</label>
                        <input type="text" value={voucherForm.code} onChange={e => setVoucherForm(prev => ({ ...prev, code: e.target.value.toUpperCase() }))} className="input w-full mt-1" disabled={voucherEditing} />
                      </div>
                      <div>
                        <label className="label">Loại</label>
                        <select value={voucherForm.type} onChange={e => setVoucherForm(prev => ({ ...prev, type: e.target.value as 'percent' | 'days' | 'plan_upgrade' }))} className="input w-full mt-1">
                          <option value="percent">Giảm %</option>
                          <option value="days">Tặng ngày</option>
                          <option value="plan_upgrade">Nâng cấp gói</option>
                        </select>
                      </div>
                      <div>
                        <label className="label">Giá trị</label>
                        <input type="number" value={voucherForm.value} onChange={e => setVoucherForm(prev => ({ ...prev, value: Number(e.target.value) }))} className="input w-full mt-1" min="1" />
                      </div>
                      <div>
                        <label className="label">Gói đích</label>
                        <select value={voucherForm.target_plan || ''} onChange={e => setVoucherForm(prev => ({ ...prev, target_plan: (e.target.value || null) as 'plus' | 'pro' | null }))} className="input w-full mt-1">
                          <option value="">Tất cả / Không áp dụng</option>
                          <option value="plus">Plus</option>
                          <option value="pro">Pro</option>
                        </select>
                      </div>
                      <div>
                        <label className="label">Số lượt tối đa</label>
                        <input type="number" value={voucherForm.max_uses} onChange={e => setVoucherForm(prev => ({ ...prev, max_uses: Number(e.target.value) }))} className="input w-full mt-1" min="1" />
                      </div>
                      <div>
                        <label className="label">Giới hạn mỗi người</label>
                        <input type="number" value={voucherForm.per_user_limit} onChange={e => setVoucherForm(prev => ({ ...prev, per_user_limit: Number(e.target.value) }))} className="input w-full mt-1" min="1" />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="label">Có hiệu lực từ</label>
                        <input type="datetime-local" value={new Date(voucherForm.valid_from).toISOString().slice(0, 16)} onChange={e => setVoucherForm(prev => ({ ...prev, valid_from: new Date(e.target.value).getTime() }))} className="input w-full mt-1" />
                      </div>
                      <div>
                        <label className="label">Hết hạn</label>
                        <input type="datetime-local" value={new Date(voucherForm.valid_until).toISOString().slice(0, 16)} onChange={e => setVoucherForm(prev => ({ ...prev, valid_until: new Date(e.target.value).getTime() }))} className="input w-full mt-1" />
                      </div>
                    </div>
                    <div>
                      <label className="label">Áp dụng cho gói (chọn nhiều)</label>
                      <div className="flex gap-4 mt-1">
                        {(['plus', 'pro'] as const).map(p => (
                          <label key={p} className="flex items-center gap-2">
                            <input type="checkbox" checked={voucherForm.metadata?.applicable_plans?.includes(p)} onChange={e => setVoucherForm(prev => ({ ...prev, metadata: { ...prev.metadata, applicable_plans: e.target.checked ? [...(prev.metadata?.applicable_plans || []), p] : (prev.metadata?.applicable_plans || []).filter((x: 'plus' | 'pro') => x !== p) } }))} />
                            {p.toUpperCase()}
                          </label>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="label">Số tiền tối thiểu (VND)</label>
                      <input type="number" value={voucherForm.metadata?.min_amount || 0} onChange={e => setVoucherForm(prev => ({ ...prev, metadata: { ...prev.metadata, min_amount: Number(e.target.value) } }))} className="input w-full mt-1 max-w-xs" />
                    </div>
                    {voucherError && <div className="text-red-600 bg-red-50 p-3 rounded">{voucherError}</div>}
                    <div className="flex gap-4 justify-end pt-4 border-t border-border">
                      <button onClick={() => setVoucherModalOpen(false)} className="btn-outline">Hủy</button>
                      <button onClick={handleVoucherSave} disabled={voucherSaving} className="btn-primary">{voucherSaving ? 'Đang lưu...' : 'Lưu'}</button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'audit' && (
          <div className="card">
            {auditError && <div className="m-4 p-4 text-red-600 bg-red-50 rounded-lg">{auditError}</div>}
            <div className="p-6 border-b border-border">
              <div className="flex flex-col sm:flex-row gap-4 mb-4">
                <div className="min-w-[200px]">
                  <label className="label">Lọc user ID</label>
                  <input type="text" value={auditUserFilter} onChange={e => { setAuditUserFilter(e.target.value); setAuditPage(1); loadAuditLogs(); }} className="input w-full mt-1" placeholder="User ID..." />
                </div>
                <div className="min-w-[200px]">
                  <label className="label">Lọc action</label>
                  <input type="text" value={auditActionFilter} onChange={e => { setAuditActionFilter(e.target.value); setAuditPage(1); loadAuditLogs(); }} className="input w-full mt-1" placeholder="Action..." />
                </div>
              </div>
            </div>
            <div className="overflow-x-auto">
              {auditLoading ? (
                <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-4 border-primary border-t-transparent" /></div>
              ) : auditLogs.length === 0 ? (
                <div className="text-center py-12 text-text-muted">Không có audit logs</div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-text-muted border-b border-border bg-gray-50">
                      <th className="p-4 font-medium">ID</th>
                      <th className="p-4 font-medium">User</th>
                      <th className="p-4 font-medium">Action</th>
                      <th className="p-4 font-medium">Resource</th>
                      <th className="p-4 font-medium">Details</th>
                      <th className="p-4 font-medium">Thời gian</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditLogs.map(log => (
                      <tr key={log.id} className="border-b border-border/50 hover:bg-gray-50">
                        <td className="p-4 font-mono text-text-muted">{log.id.slice(0, 8)}...</td>
                        <td className="p-4">{log.user_id}</td>
                        <td className="p-4 font-mono text-sm">{log.action}</td>
                        <td className="p-4">{log.resource_id}</td>
                        <td className="p-4 max-w-xs">
                          <pre className="text-xs bg-gray-100 p-2 rounded overflow-auto max-h-24">{JSON.stringify(log.details, null, 2)}</pre>
                        </td>
                        <td className="p-4 text-text-muted">{formatDate(log.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            {/* Pagination */}
            {!auditLoading && auditTotal > 0 && Math.ceil(auditTotal / auditPageSize) > 1 && (
              <div className="p-6 border-t border-border flex items-center justify-between">
                <div className="text-text-muted text-sm">Hiển thị {(auditPage - 1) * auditPageSize + 1} - {Math.min(auditPage * auditPageSize, auditTotal)} của {auditTotal}</div>
                <div className="flex gap-2">
                  <button onClick={() => setAuditPage(p => Math.max(1, p - 1))} disabled={auditPage === 1} className="btn-outline text-sm">Trước</button>
                  <span className="flex items-center px-3 text-sm text-text">Trang {auditPage} / {Math.ceil(auditTotal / auditPageSize)}</span>
                  <button onClick={() => setAuditPage(p => Math.min(Math.ceil(auditTotal / auditPageSize), p + 1))} disabled={auditPage === Math.ceil(auditTotal / auditPageSize)} className="btn-outline text-sm">Sau</button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}